/**
 * useCollapsedState
 *
 * Hook for managing sidebar collapse state with localStorage persistence.
 * Pattern mirrors useColumnPreferences from MetadataTable.
 */

import { useState, useCallback } from 'react'

const STORAGE_PREFIX = 'rp.sidebar.'

interface UseCollapsedStateOptions {
  /** localStorage key suffix (e.g., 'dataPage.sidebar') */
  storageKey?: string
  /** Initial collapsed state if not in storage (default: false = expanded) */
  defaultCollapsed?: boolean
  /** Callback when collapse state changes */
  onCollapseChange?: (collapsed: boolean) => void
}

interface UseCollapsedStateReturn {
  /** Current collapsed state */
  collapsed: boolean
  /** Set collapsed state (persists to localStorage) */
  setCollapsed: (value: boolean) => void
  /** Toggle collapsed state */
  toggleCollapsed: () => void
}

/**
 * Manages sidebar collapse state with localStorage persistence.
 *
 * @example
 * ```tsx
 * const { collapsed, toggleCollapsed } = useCollapsedState({
 *   storageKey: 'dataPage.sidebar',
 *   defaultCollapsed: false,
 * })
 * ```
 */
export function useCollapsedState({
  storageKey,
  defaultCollapsed = false,
  onCollapseChange,
}: UseCollapsedStateOptions = {}): UseCollapsedStateReturn {
  // Initialize from localStorage if key provided
  const [collapsed, setCollapsedState] = useState<boolean>(() => {
    if (!storageKey) return defaultCollapsed
    try {
      const stored = localStorage.getItem(STORAGE_PREFIX + storageKey)
      return stored !== null ? stored === 'true' : defaultCollapsed
    } catch {
      return defaultCollapsed
    }
  })

  // Persist to localStorage and notify parent
  const setCollapsed = useCallback(
    (value: boolean) => {
      setCollapsedState(value)

      if (storageKey) {
        try {
          localStorage.setItem(STORAGE_PREFIX + storageKey, String(value))
        } catch {
          // localStorage unavailable - continue without persistence
        }
      }

      onCollapseChange?.(value)
    },
    [storageKey, onCollapseChange]
  )

  const toggleCollapsed = useCallback(() => {
    setCollapsed(!collapsed)
  }, [collapsed, setCollapsed])

  return { collapsed, setCollapsed, toggleCollapsed }
}
